/**
 * usePermissions Hook
 * Provides permission checking functionality for components
 */

import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { RolePermissions, PermissionLevel, PageKey, hasPermission } from '../types/permissions';
import { permissionService } from '../services/permissionService';

interface UsePermissionsReturn {
  permissions: RolePermissions | null;
  loading: boolean;
  error: Error | null;
  hasAccess: (page: PageKey, level: PermissionLevel) => boolean;
  canRead: (page: PageKey) => boolean;
  canWrite: (page: PageKey) => boolean;
  canCreate: (page: PageKey) => boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook to get and check user permissions
 */
export const usePermissions = (user?: User, orgId?: string): UsePermissionsReturn => {
  const [permissions, setPermissions] = useState<RolePermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadPermissions = useCallback(async () => {
    if (!user || !orgId) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const effectivePermissions = await permissionService.getUserEffectivePermissions(user, orgId);
      setPermissions(effectivePermissions);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err instanceof Error ? err : new Error('Failed to load permissions'));
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.role, user?.customRoleId, orgId]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasAccess = useCallback((page: PageKey, level: PermissionLevel): boolean => {
    if (!permissions) return false;
    const userLevel = permissions[page];
    return hasPermission(userLevel, level);
  }, [permissions]);

  const canRead = useCallback((page: PageKey): boolean => {
    return hasAccess(page, 'read');
  }, [hasAccess]);

  const canWrite = useCallback((page: PageKey): boolean => {
    return hasAccess(page, 'write');
  }, [hasAccess]);

  const canCreate = useCallback((page: PageKey): boolean => {
    return hasAccess(page, 'create');
  }, [hasAccess]);

  const refresh = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  return {
    permissions,
    loading,
    error,
    hasAccess,
    canRead,
    canWrite,
    canCreate,
    refresh,
  };
};

